import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "./AuthProvider";
import {
    getNotifications,
    markAllNotificationsRead,
    markNotificationRead,
} from "../helpers/httpRequests/notifications";

export const NotificationsContext = createContext({});

const POLL_MS = 30000;

export function NotificationsProvider({ children }) {
    const { isAuthenticated, user } = useAuth();
    const customerId = user?.id;
    const [items, setItems] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const timerRef = useRef(null);

    const refreshNotifications = useCallback(async () => {
        if (!isAuthenticated || !customerId) {
            setItems([]);
            setUnreadCount(0);
            return;
        }

        try {
            const response = await getNotifications({ limit: 15, offset: 0 });
            const data = response?.data?.data;
            setItems(Array.isArray(data?.items) ? data.items : []);
            setUnreadCount(Number(data?.unread_count) || 0);
        } catch {
            // silencioso
        }
    }, [isAuthenticated, customerId]);

    const markAsRead = useCallback(async (notificationId) => {
        const target = items.find((n) => n.id === notificationId);
        const wasUnread = target && !target.is_read;
        try {
            await markNotificationRead(notificationId);
            setItems((prev) =>
                prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
            );
            if (wasUnread) {
                setUnreadCount((prev) => Math.max(0, prev - 1));
            }
        } catch {
            // ignore
        }
    }, [items]);

    const markAllAsRead = useCallback(async () => {
        try {
            await markAllNotificationsRead();
            setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
            setUnreadCount(0);
        } catch {
            // ignore
        }
    }, []);

    useEffect(() => {
        if (!isAuthenticated || !customerId) {
            setItems([]);
            setUnreadCount(0);
            return undefined;
        }

        refreshNotifications();
        timerRef.current = setInterval(refreshNotifications, POLL_MS);

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [isAuthenticated, customerId, refreshNotifications]);

    return (
        <NotificationsContext.Provider
            value={{
                items,
                unreadCount,
                refreshNotifications,
                markAsRead,
                markAllAsRead,
            }}
        >
            {children}
        </NotificationsContext.Provider>
    );
}

export function useNotifications() {
    return useContext(NotificationsContext);
}
